import { Button } from '@/components/ui/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu/DropdownMenu';
import { useTimeFor } from '@/hooks/useTimeFor';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import type { Appointment } from '@/types/scheduling';
import { Calendar, CalendarPlus, Ellipsis, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCancelFlow } from '../CancelDialog/context/useCancelFlow';
import { useRescheduleFlow } from '../RescheduleDialog/context/useRescheduleFlow';
import { generateCalendarFile } from '@/utils/generateCalendarFile.ts';
import { captureError } from '@/config/sentry.ts';
import { toast } from '@/components/ui/Toast/Toast';
import { useAuthStore } from '@/stores/authStore.ts';
import { getStudentEnrollmentDetails } from '@/features/scheduling/utils/getStudentEnrollmentDetails';
import { isEnrollmentSchedulable } from '@/features/scheduling/utils/schedulability';
import { useIsSchedulingBlocked } from '@/features/systemBanner/hooks/useIsSchedulingBlocked';

interface SessionItemDropdownProps {
  appointment: Appointment;
  openButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

export const SessionItemDropdown = ({ appointment, openButtonRef }: SessionItemDropdownProps) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'yourSessions' });
  const { t: tCalendar } = useTranslation('scheduling', { keyPrefix: 'addToCalendarData' });
  const { t: tPaused } = useTranslation('scheduling', { keyPrefix: 'schedulingPaused' });
  const isSchedulingBlocked = useIsSchedulingBlocked();

  const { userData } = useAuthStore();
  const { timeZone: calendarTimeZone } = useTimeFor('calendarExport');
  const students = userData?.students ?? [];

  const { data, openRescheduleDialog } = useRescheduleFlow();
  const { openCancelDialog } = useCancelFlow();

  const studentSchedulingDetails = data.config.data?.students.find((student) => student.id === appointment.student_id);
  const appointmentEnrollment =
    studentSchedulingDetails && getStudentEnrollmentDetails(data.config.data?.students ?? [], appointment.student_id, appointment.enrollment_id);
  const canReschedule =
    studentSchedulingDetails !== undefined && appointmentEnrollment !== undefined && isEnrollmentSchedulable(appointmentEnrollment, studentSchedulingDetails);

  const handleRescheduleClick = () => {
    if (isSchedulingBlocked) return;

    void captureAnalyticsEvent('rescheduling_started', { appointment_id: appointment.appointment_id });
    openRescheduleDialog(appointment, openButtonRef);
  };
  const handleCancelClick = () => {
    if (isSchedulingBlocked) return;

    void captureAnalyticsEvent('cancelling_started', { appointment_id: appointment.appointment_id });
    openCancelDialog(appointment, openButtonRef);
  };

  const handleAddToCalendar = () => {
    try {
      const student = students.find((student) => student.id === appointment.student_id);

      const appointmentStudentConfig = data.config.data?.students.find((studentConfig) => studentConfig.id === appointment.student_id);
      const appointmentEnrollment = appointmentStudentConfig?.enrollments.find((enrollment) => enrollment.id === appointment?.enrollment_id);

      const title = tCalendar('sessionTitle', {
        sessionName: appointmentEnrollment?.name,
        studentFirstName: student?.firstName,
        studentLastName: student?.lastName,
      });

      const description = tCalendar('sessionDescription', {
        studentFirstName: student?.firstName,
        studentLastName: student?.lastName,
      });

      const location = userData?.center?.address ?? tCalendar('defaultLocation');

      const organizer = { name: userData?.center?.name, email: userData?.center?.email };

      generateCalendarFile({ appointment, students, title, description, location, organizer, notBookedAppointments: [], timeZone: calendarTimeZone });
    } catch (error) {
      captureError(error);

      toast({
        description: tCalendar('addToCalendarErrorToast.description'),
        variant: 'destructive',
      });
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        className='data-[state=open]:bg-fill-button-action-icon-active data-[state=open]:text-text-brand-primary'
        ref={openButtonRef}
      >
        <Button
          variant='icon'
          size='icon-l'
          className='hover:bg-fill-button-action-icon-active active:bg-fill-button-action-icon-active hover:text-text-brand-primary'
          aria-label={t('dropdown.ariaLabel')}
        >
          <Ellipsis size={24} aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-56' align='end'>
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={handleAddToCalendar}>
            <CalendarPlus className='text-text-default-secondary mr-2' size={20} /> <span>{t('dropdown.addToCalendar')}</span>
          </DropdownMenuItem>
          {canReschedule && (
            <DropdownMenuItem onClick={handleRescheduleClick} disabled={isSchedulingBlocked}>
              <Calendar className='text-text-default-secondary mr-2' size={20} /> <span>{t('dropdown.reschedule')}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={handleCancelClick} disabled={isSchedulingBlocked} className='text-text-destructive [&>svg]:stroke-text-destructive'>
            <Trash2 className='text-text-default-secondary mr-2' size={20} />
            <span>{t('dropdown.cancel')}</span>
          </DropdownMenuItem>
          {isSchedulingBlocked && (
            <DropdownMenuLabel className='text-body-s-regular text-text-default-secondary font-normal'>{tPaused('menuNote')}</DropdownMenuLabel>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
